import React from "react";
import { Input, Select, SelectOption, Switch } from "@showitinc/showit-wc";

import { formatPropUpdate } from "../../../utils/PropertyUtils";
import { initFormatter } from "../../../utils/PropertyFormatters";
import { deleteProp } from "../../../utils/PropUpdateUtils";
import { INPUT_TYPE_NUMBER } from "../../../constants/ElementConstants";
import {
  WINDOW_HEIGHT,
  GROW_WITH_CONTENT,
  SCROLLING_VIEWS,
  SPACING_CONDENSED,
  SPACING_WINDOW,
} from "../../../constants/CanvasTypeConstants.mjs";
import AppConstants from "../../../constants/AppConstants";
import UserActions from "../../../actions/UserActions";
import useV2Feature from "@/hooks/useV2Feature";
import { SCROLLING_VIEWS_FLAG } from "@/constants/FeatureFlags.mjs";

const { BLOCK_PROP_PARENT_BLOCK } = AppConstants;

/**
 * @typedef {import("../../../vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} Layouts
 * @typedef {Layouts[number]} Layout
 */

// todo: shift these cleanup functions into their own directory eventually. I'm considering a pattern where we can have those isolated so cleanups are always easy to reach when needed
/**
 * PropUpdates that clear data invalidated by a canvas type change.
 * @param {Layout} layout
 * @param {string} prevNature
 * @param {string} nextNature
 * @returns {PropUpdate[]}
 */
export const getNatureChangeCleanup = (layout, prevNature, nextNature) =>
  nextNature === SCROLLING_VIEWS
    ? [deleteProp(`${layout}.locking`)]
    : prevNature === SCROLLING_VIEWS
      ? [
          deleteProp(`${layout}.snap`),
          deleteProp(`${layout}.direction`),
          deleteProp(`${layout}.spacing`),
        ]
      : [];

/**
 * @callback OnPropUpdate
 * @param {PropUpdate | PropUpdate[]} update
 * @returns {void}
 *
 * @param {Object} props
 * @param {boolean} props.usingSiteBlock
 * @param {Layout} props.layout
 * @param {*} props.block - needs schema typing
 * @param {OnPropUpdate} props.onPropUpdate
 * @returns {React.ReactElement}
 */
const BlockPropertiesSize = ({ usingSiteBlock = false, layout, block, onPropUpdate }) => {
  const blockLayout = block.get(layout);
  const height = blockLayout.get("h", 0);
  const nature = blockLayout.get("nature", "");
  const locking = blockLayout.getIn(["locking", "side"], "");
  const hide = blockLayout.get("hide", false);
  const snap = blockLayout.get("snap", false);
  const direction = blockLayout.get("direction", "left");
  const spacing = blockLayout.get("spacing", SPACING_CONDENSED);

  const canUseScrollingViews = useV2Feature(SCROLLING_VIEWS_FLAG);

  // A canvas using a site canvas inherits its type from the site block
  const effectiveNature = usingSiteBlock
    ? block.getIn([BLOCK_PROP_PARENT_BLOCK, layout, "nature"], "")
    : nature;
  const isScrollingViews = effectiveNature === SCROLLING_VIEWS;

  /** @param {PropUpdate | PropUpdate[]} propInfo */
  const _onPropChange = (propInfo) => {
    if (onPropUpdate) {
      onPropUpdate(propInfo);
    }
  };

  /** @param {PropUpdate} propInfo */
  const _onCheckChange = (propInfo) => {
    _onPropChange({ ...propInfo, value: propInfo.value ? 1 : null });
  };

  /** @param {PropUpdate} propInfo */
  const _onNatureUpdate = (propInfo) => {
    _onPropChange([...getNatureChangeCleanup(layout, nature, propInfo.value), propInfo]);
    if (propInfo.value === SCROLLING_VIEWS && nature !== SCROLLING_VIEWS) {
      UserActions.trackPostHogEvent("scrolling_views_enabled", { layout, spacing });
    }
  };

  /** @param {PropUpdate} propInfo */
  const _onSpacingUpdate = (propInfo) => {
    _onPropChange(propInfo);
    if (propInfo.value !== spacing) {
      UserActions.trackPostHogEvent("scrolling_views_spacing_changed", {
        layout,
        spacing: propInfo.value,
      });
    }
  };

  const { onChange: onNatureChange } = initFormatter(_onNatureUpdate);
  const { onChange: onSelectChange } = initFormatter(_onPropChange);
  const { onChange: onSpacingChange } = initFormatter(_onSpacingUpdate);

  const heightLabel = nature === WINDOW_HEIGHT ? "Minimum Height" : "Initial Height";

  return (
    <>
      {!usingSiteBlock && (
        <>
          <Select
            data-testid="canvas-type-select"
            label="Canvas Type"
            helpTooltip="canvas-type"
            value={nature}
            shift="left"
            reserveContext
            onSISelect={onNatureChange(`${layout}.nature`, nature)}
          >
            <SelectOption value="">Normal</SelectOption>
            <SelectOption value={WINDOW_HEIGHT}>Window Height</SelectOption>
            <SelectOption value={GROW_WITH_CONTENT}>Grow with Content</SelectOption>
            {canUseScrollingViews ? (
              <SelectOption value={SCROLLING_VIEWS}>Scrolling Canvas Views</SelectOption>
            ) : null}
          </Select>
          <Input
            type={INPUT_TYPE_NUMBER}
            min="1"
            max="300000"
            step="10"
            units="px"
            label={heightLabel}
            helpTooltip="canvas-height"
            value={height}
            onSIBeforeChange={(e) =>
              _onPropChange(formatPropUpdate(`${layout}.h`, +e.target.value, height))
            }
            onSIInput={(e) =>
              _onPropChange(formatPropUpdate(`${layout}.h`, +e.target.value, height, null, false))
            }
          />
        </>
      )}

      {!isScrollingViews && (
        <>
          <Select
            label="Sticky"
            value={locking}
            shift="left"
            reserveContext
            onSISelect={(e) =>
              _onPropChange(formatPropUpdate(`${layout}.locking.side`, e.target.value, locking))
            }
          >
            <SelectOption value="">None</SelectOption>
            <SelectOption value="t">Top</SelectOption>
            <SelectOption value="b">Bottom</SelectOption>
            <SelectOption value="st">Top on Scroll</SelectOption>
            <SelectOption value="sb">Bottom on Scroll</SelectOption>
          </Select>
          {locking && (
            <Input
              type={INPUT_TYPE_NUMBER}
              step="10"
              units="px"
              value={block.getIn([layout, "locking", "offset"], 0)}
              label="Offset"
              onSIBeforeChange={(e) =>
                _onPropChange(
                  formatPropUpdate(
                    `${layout}.locking.offset`,
                    e.target.value,
                    block.getIn([layout, "locking", "offset"]),
                  ),
                )
              }
              onSIInput={(e) =>
                _onPropChange(
                  formatPropUpdate(
                    `${layout}.locking.offset`,
                    e.target.value,
                    block.getIn([layout, "locking", "offset"]),
                    null,
                    false,
                  ),
                )
              }
            />
          )}
          {(locking === "t" || locking === "st") && (
            <Switch
              checked={block.getIn([layout, "locking", "scrollOffset"], 0)}
              helpTooltip="canvas-scroll-links-under"
              label="Scroll Links Under"
              onClick={(e) =>
                _onCheckChange(
                  formatPropUpdate(
                    `${layout}.locking.scrollOffset`,
                    e.target.checked,
                    block.getIn([layout, "locking", "scrollOffset"]),
                  ),
                )
              }
            />
          )}
        </>
      )}
      {/* Direction, snap and spacing belong to the site canvas definition; an instance's own copies are never rendered */}
      {isScrollingViews && !usingSiteBlock && (
        <>
          <Select
            label="Direction"
            value={direction}
            shift="left"
            reserveContext
            onSISelect={onSelectChange(`${layout}.direction`, direction)}
          >
            <SelectOption value="left">Left</SelectOption>
            <SelectOption value="right">Right</SelectOption>
            <SelectOption value="up">Up</SelectOption>
            <SelectOption value="down">Down</SelectOption>
          </Select>
          <Select
            label="Spacing"
            value={spacing}
            shift="left"
            reserveContext
            onSISelect={onSpacingChange(`${layout}.spacing`, spacing)}
          >
            <SelectOption value={SPACING_CONDENSED}>Condensed</SelectOption>
            <SelectOption value={SPACING_WINDOW}>Window</SelectOption>
          </Select>
          <Switch
            checked={snap}
            label="Snap"
            onClick={(e) =>
              _onCheckChange(formatPropUpdate(`${layout}.snap`, e.target.checked, snap))
            }
          />
        </>
      )}
      <Switch
        checked={hide}
        helpTooltip="canvas-hidden"
        label="Hidden to start"
        onClick={(e) => _onCheckChange(formatPropUpdate(`${layout}.hide`, e.target.checked, hide))}
      />
    </>
  );
};

export default BlockPropertiesSize;
